import { useEffect } from "react";

import {
  Button,
  CheckCircleFilledSVG,
  Icon,
  Input,
  LoadingSpinner,
  RadioButton,
} from "@newsela/angelou";
import PropTypes from "prop-types";

import GoogleIcon from "@/icons/GoogleIcon";
import useValidateUserEmail from "@/users/useValidateUserEmail";
import {
  googleButtonCss,
  googleIconCss,
  newEmailContainerCss,
  newEmailInputCss,
  newEmailSeparatorCss,
  radioButtonCss,
  newEmailInputContainerCss,
  newEmailInputSpinnerCss,
  newEmailInputValidIconCss,
  googleButtonContainerCss,
  newEmailSeparatorContainerCss,
} from "./ConfirmEmailAddress.style";

const propTypes = {
  /**
   * User information
   */
  user: PropTypes.shape({
    email: PropTypes.string.isRequired,
    _editable: PropTypes.shape({
      email: PropTypes.string,
    }).isRequired,
  }).isRequired,
  /**
   * Function to handle form changes
   */
  onChange: PropTypes.func.isRequired,
  /**
   * Values from the form changes
   */
  form: PropTypes.shape({
    isCurrentEmailValid: PropTypes.bool,
    newEmail: PropTypes.string,
  }).isRequired,
  /**
   * On click submit via Google
   */
  onSubmit: PropTypes.func.isRequired,
};

const ConfirmEmailAddress = ({
  user,
  onChange,
  form: { isCurrentEmailValid, newEmail },
  onSubmit,
}) => {
  const {
    error: emailError,
    isLoading,
    isAvailable,
  } = useValidateUserEmail(newEmail);

  useEffect(() => {
    onChange({ isNewEmailValid: !emailError });
  }, [emailError]);

  return (
    <>
      <p>
        Is <strong>{user.email}</strong> your new faculty email address?
      </p>
      <RadioButton
        value="Yes"
        label="Yes"
        name="email"
        onChange={() => onChange({ isCurrentEmailValid: true, newEmail: "" })}
        checked={isCurrentEmailValid}
        __cssFor={radioButtonCss}
      />
      <RadioButton
        value="No"
        label="No"
        name="email"
        onChange={() => onChange({ isCurrentEmailValid: false, newEmail: "" })}
        checked={isCurrentEmailValid === false}
        __cssFor={radioButtonCss}
      />
      {isCurrentEmailValid === false && (
        <div css={newEmailContainerCss}>
          <div css={newEmailInputContainerCss}>
            <Input
              label="New faculty email address"
              placeholder="Start typing..."
              inputType={Input.inputTypes.text}
              __cssFor={newEmailInputCss}
              onChange={(e) => onChange({ newEmail: e.target.value })}
              touched={Boolean(newEmail)}
              value={newEmail}
              error={emailError}
              disabled={!user._editable.email}
              helperText={
                user._editable.email ? "" : "This field is read-only."
              }
            />
            {isLoading && <LoadingSpinner __cssFor={newEmailInputSpinnerCss} />}
            {isAvailable && (
              <Icon
                SvgComponent={CheckCircleFilledSVG}
                __cssFor={newEmailInputValidIconCss}
              />
            )}
          </div>
          <div css={newEmailSeparatorContainerCss}>
            <span css={newEmailSeparatorCss}>Or</span>
          </div>
          <div css={googleButtonContainerCss}>
            <Button
              flavor={Button.flavors.primary}
              icon={{
                SvgComponent: GoogleIcon,
                alignment: Button.iconAlignments.left,
              }}
              __cssFor={{
                root: googleButtonCss,
                Icon: { root: googleIconCss },
              }}
              onClick={onSubmit}
            >
              Verify Google email
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

ConfirmEmailAddress.propTypes = propTypes;

export default ConfirmEmailAddress;
